import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { IEnrollMFAPayload, IMFAConfigureResponseData, ITenantInfo } from '../../../shared/model/IMfa.model';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { IError, IErrorData, IUiError } from '../../../shared/model/IError.model';
import { SharedService } from '../../../shared/shared.service';
import { takeUntil } from 'rxjs/operators';

interface IIvrData{
  mobile_number:string;
}

interface IOtpData{
  otp:string;
}

@Component({
  selector: 'verification-enroll-ivr',
  templateUrl: './ivr.component.html',
  styleUrls: ['./ivr.component.scss'],
})
export class IvrComponent implements OnInit, OnDestroy {
  @Input() config!: ITenantInfo;
  @Input() track_id!: string;
  @Output() checkEnrollmentStatus = new EventEmitter();

  // will control the screen flow [screen1, screen2, screen3]
  page$ = new BehaviorSubject('screen1');

  // enrollment payload
  payload: IEnrollMFAPayload = {} as IEnrollMFAPayload;

  // the flg is to handle the dynamic form flow
  isSubmitBtnClicked = false;

  // will hold the enrollment success respose
  successState$ = new BehaviorSubject<IMFAConfigureResponseData>({});

  //will hold the error
  errorState$ = new BehaviorSubject(<IUiError>{});

  // will use to unsubscribe the observables
  destroy$ = new Subject();

  // forms
  forms = {
    mobile: {
      fields: [
        {
          fieldKey: 'mobile_number',
          dataType: 'MOBILE',
          required: true,
          enabled: true,
          fieldDefinition: {},
          localeText: {
            locale: 'en-us',
            language: 'en',
            name: this.translate.instant('SUGGESTED_VERIFICATION_METHOD.ENROLLMENT.IVR.FORM.MOBILE.LABEL'),
            placeholder: this.translate.instant('SUGGESTED_VERIFICATION_METHOD.ENROLLMENT.IVR.FORM.MOBILE.PLACEHOLDER'),
            hint: this.translate.instant('SUGGESTED_VERIFICATION_METHOD.ENROLLMENT.IVR.FORM.MOBILE.HINT'),
          },
        },
      ],
      formOptions: {
        btnEnabled: false,
      },
      buttons: {
        cancel: {
          color: 'accent',
          text: 'SUGGESTED_VERIFICATION_METHOD.CANCEL',
          alignment: 'right',
          type: 'proceed',
          underline: 'none',
          for: 'cidaasCancelBtn',
          translateArgs: {},
          style: 'secondary',
        },
        submit: {
          color: 'accent',
          text: 'SUGGESTED_VERIFICATION_METHOD.CONTINUE',
          alignment: 'right',
          type: 'proceed',
          underline: 'none',
          for: 'cidaasSubmitBtn',
          translateArgs: {},
        },
      },
      data: null,
    },
    otp: {
      fields: [
        {
          fieldKey: 'otp',
          dataType: 'TEXT',
          required: true,
          enabled: true,
          fieldDefinition: {},
          localeText: {
            locale: 'en-us',
            language: 'en',
            name: this.translate.instant('SUGGESTED_VERIFICATION_METHOD.ENROLLMENT.IVR.FORM.OTP.LABEL'),
            placeholder: this.translate.instant('SUGGESTED_VERIFICATION_METHOD.ENROLLMENT.IVR.FORM.OTP.PLACEHOLDER'),
            hint: this.translate.instant('SUGGESTED_VERIFICATION_METHOD.ENROLLMENT.IVR.FORM.OTP.HINT'),
          },
        },
      ],
      formOptions: {
        btnEnabled: false,
      },
      buttons: {
        cancel: {
          color: 'accent',
          text: 'SUGGESTED_VERIFICATION_METHOD.CANCEL',
          alignment: 'right',
          type: 'proceed',
          underline: 'none',
          for: 'cidaasCancelBtn',
          translateArgs: {},
          style: 'secondary',
        },
        submit: {
          color: 'accent',
          text: 'SUGGESTED_VERIFICATION_METHOD.CONTINUE',
          alignment: 'right',
          type: 'proceed',
          underline: 'none',
          for: 'cidaasSubmitBtn',
          translateArgs: {},
        },
      },
      data: null,
    },
  };

  constructor(private translate: TranslateService, private _sharedService: SharedService) {}

  ngOnInit(): void {}
  ngOnDestroy(): void {
    this.destroy$.next(true);
  }

  _cancelIVRProcess = () => {
    this.checkEnrollmentStatus.emit(false);
  };

  _switchPage = (screen: string) => {
    this.page$.next(screen);
  };

  _initiateIVRProcess = (data:IIvrData|null) => {
    if (data) {
      this._sharedService._initiateSuggestVerification(this.track_id, 'ivr', this._constructInitiatePayload(data)).subscribe(
        (res) => {
          this.payload.exchange_id = res?.exchange_id?.exchange_id;
          this.payload.sub = res?.sub;
          // enable otp screen
          this._switchPage('screen2');
        },
        (err) => {

          // preparing the error data for the UI
          const default_message = 'SUGGESTED_VERIFICATION_METHOD.ENROLLMENT.IVR.ERROR_INITIATE.DEFAULT';
          const message =  (err.statusCode === 409) && `SUGGESTED_VERIFICATION_METHOD.ENROLLMENT.IVR.ERROR_INITIATE.STATUS_${err.statusCode}`
                || default_message;
          this.errorState$.next({message, default_message, status: err.statusCode});
        }
      );
    }
  };

  _constructInitiatePayload = (data: IIvrData) => {
    return { phone: data?.mobile_number };
  };

  _verifyIVREnrolment = (data:IOtpData) => {
    this.payload.pass_code = data.otp;
    this._sharedService._configureSuggestVerification(this.payload, 'ivr').subscribe((data) => {
      this.successState$.next(data);
      this._switchPage('screen3');
    }, (err)=>{
      // preparing the error data for the UI
      const default_message = 'SUGGESTED_VERIFICATION_METHOD.ENROLLMENT.IVR.ERROR_CONFIGURE';
      const message = err.statusCode
            &&`SUGGESTED_VERIFICATION_METHOD.ENROLLMENT.IVR.ERROR_CONFIGURE`
            || default_message;
      this.errorState$.next({message, default_message, status: err?.statusCode});
    });
  };

  _completeIVRProcess = () => {
    this.successState$.pipe(takeUntil(this.destroy$)).subscribe((res) => {
      const state = { status: 'completed', data: res };
      this.checkEnrollmentStatus.emit(state);
    });
  };
}
